import { useEffect, useState } from 'react';

import { supabase } from '@/integrations/supabase/client';

export interface CurrentUserProfile {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

const PROFILE_CACHE_TTL_MS = 30_000;
const EMPTY_PROFILE: CurrentUserProfile = {
  first_name: null,
  last_name: null,
  avatar_url: null,
};

const profileCache = new Map<string, { data: CurrentUserProfile; expiresAt: number }>();
const profileInflight = new Map<string, Promise<CurrentUserProfile>>();

async function fetchCurrentUserProfile(userId: string) {
  const cached = profileCache.get(userId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  let inflight = profileInflight.get(userId);
  if (!inflight) {
    inflight = supabase
      .from('profiles')
      .select('first_name, last_name, avatar_url')
      .eq('user_id', userId)
      .maybeSingle<CurrentUserProfile>()
      .then(({ data, error }) => {
        if (error) {
          throw error;
        }

        const nextProfile = data ?? EMPTY_PROFILE;
        profileCache.set(userId, {
          data: nextProfile,
          expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
        });
        return nextProfile;
      })
      .finally(() => {
        profileInflight.delete(userId);
      });

    profileInflight.set(userId, inflight);
  }

  return inflight;
}

export function resetCurrentUserProfileCacheForTests() {
  profileCache.clear();
  profileInflight.clear();
}

export function useCurrentUserProfile(userId?: string) {
  const [profile, setProfile] = useState<CurrentUserProfile | null>(() => {
    if (!userId) {
      return null;
    }

    const cached = profileCache.get(userId);
    return cached && cached.expiresAt > Date.now() ? cached.data : null;
  });

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return;
    }

    let isCancelled = false;

    const loadProfile = async (force = false) => {
      if (force) {
        profileCache.delete(userId);
      }

      try {
        const nextProfile = await fetchCurrentUserProfile(userId);
        if (!isCancelled) {
          setProfile(nextProfile);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching current user profile:', error);
          setProfile(EMPTY_PROFILE);
        }
      }
    };

    void loadProfile();

    const handleProfileUpdate = () => {
      void loadProfile(true);
    };

    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => {
      isCancelled = true;
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [userId]);

  return profile;
}
